import type { BusinessEvent, Event, Offer, Thing, WithContext } from 'schema-dts';
import type { Question } from '@/entities/question';
import type { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import { generateFaqPageJsonLd } from '@/shared/lib/utils/faq-json-ld';
import {
    type SchemaContacts,
    type SchemaOfferInput,
    buildSchemaOffers,
    buildSchemaPlace,
    generateBreadcrumbListJsonLd,
    oneLine,
    schemaEventImage,
    schemaIdRef,
    stripHtml,
} from '@/shared/lib/utils/schema-common';
import {
    SCHEMA_EVENT_ATTENDANCE,
    SCHEMA_EVENT_STATUS,
    getSchemaIds,
    schemaAbsoluteUrl,
} from '@/shared/lib/utils/schema-ids';

export type BusinessEventVariant = 'forum' | 'expo';

export type GenerateBusinessEventJsonLdInput = {
    variant: BusinessEventVariant;
    name?: string | null;
    description?: string | null;
    disambiguatingDescription?: string | null;
    startDateIso?: string | null;
    endDateIso?: string | null;
    imageSrc?: string | null;
    offers?: SchemaOfferInput[];
    breadcrumbs: AppBreadcrumbs;
    faqQuestions?: Question[];
    contacts?: SchemaContacts | null;
};

const VARIANT_CONFIG = {
    forum: {
        fallbackName: 'TNF FORUM',
        alternateName: ['Forum', 'ТНФ Форум'],
        pagePath: '/participation/participants/',
        idKey: 'tnfForum' as const,
        hasExhibitionSubEvent: false,
    },
    expo: {
        fallbackName: 'TNF EXPO',
        alternateName: ['Expo', 'ТНФ ЭКСПО'],
        pagePath: '/participation/visitors/',
        idKey: 'tnfExpo' as const,
        hasExhibitionSubEvent: true,
    },
} as const;

export function generateBusinessEventJsonLd({
    variant,
    name,
    description,
    disambiguatingDescription,
    startDateIso,
    endDateIso,
    imageSrc,
    offers,
    breadcrumbs,
    faqQuestions,
    contacts,
}: GenerateBusinessEventJsonLdInput): WithContext<Thing>[] {
    const ids = getSchemaIds();
    const config = VARIANT_CONFIG[variant];
    const pageUrl = schemaAbsoluteUrl(config.pagePath);
    const eventImage = schemaEventImage(imageSrc);
    const schemaOffers: Offer[] = buildSchemaOffers(offers ?? []);
    const descriptionText = description ? oneLine(stripHtml(description)) : '';
    const disambiguatingText = disambiguatingDescription ? oneLine(stripHtml(disambiguatingDescription)) : '';

    const event: WithContext<BusinessEvent> = {
        '@context': 'https://schema.org',
        '@type': 'BusinessEvent',
        '@id': ids[config.idKey],
        name: oneLine(name || '') || config.fallbackName,
        alternateName: [...config.alternateName],
        url: pageUrl,
        ...(descriptionText ? { description: descriptionText } : {}),
        ...(disambiguatingText ? { disambiguatingDescription: disambiguatingText } : {}),
        ...(startDateIso ? { startDate: startDateIso } : {}),
        ...(endDateIso ? { endDate: endDateIso } : {}),
        inLanguage: 'ru',
        eventStatus: SCHEMA_EVENT_STATUS,
        eventAttendanceMode: SCHEMA_EVENT_ATTENDANCE,
        location: buildSchemaPlace(contacts),
        organizer: schemaIdRef(ids.organization) as Event['organizer'],
        superEvent: schemaIdRef(ids.forum) as Event['superEvent'],
        ...(config.hasExhibitionSubEvent ? { subEvent: schemaIdRef(ids.exhibition) as Event['subEvent'] } : {}),
        ...(eventImage ? { image: eventImage } : {}),
        ...(schemaOffers.length > 0 ? { offers: schemaOffers } : {}),
    };

    const graph: WithContext<Thing>[] = [
        event as WithContext<Thing>,
        generateBreadcrumbListJsonLd(breadcrumbs) as WithContext<Thing>,
    ];

    const faq = generateFaqPageJsonLd(faqQuestions ?? [], pageUrl);
    if (faq) graph.push(faq);

    return graph;
}
